/**
 * wizardSteps — presentation metadata for the M2 wizard strip.
 *
 * IMPORTANT (D12): this file carries ONLY labels/targets/action kinds.
 * Step completion, gating and counts come exclusively from
 * `useContentPlanFlowReadiness` (the shared evaluator). The one addition is
 * the ADVISORY "research" node (po.md step 4) — it has NO readiness logic:
 * it never completes, never gates, and renders as an "Optional" node
 * (mirrors D12's `required:false` assets semantics; DESIGN_BRIEF open Q2).
 */

import type { ContentPlanFlowStep, ContentPlanStepReadiness } from '@modules/shared-types';

/** Sub-tab ids of ContentPlansPage the wizard can deep-link (explicit clicks only). */
export type WizardSubTabId =
  | 'creator' | 'platforms'
  | 'backlog' | 'themes'
  | 'strategies' | 'calendar' | 'engagement';

/** 9-node spine: 8 D12 steps + the advisory research node. */
export type WizardStepId = ContentPlanFlowStep | 'research';

export type WizardPrimaryActionKind =
  /** Opens the author onboarding dialog (page-owned). */
  | 'onboard-author'
  /** Confirms all draft pillars / generates pillars from profile. */
  | 'pillars'
  /** Opens the GenerateTopicsDialog (wizard-owned). */
  | 'generate-topics'
  /** Opens the CreateContentStrategyDialog (page-owned). */
  | 'create-strategy'
  /** Explicit navigation CTA — same action as a user sub-tab click. */
  | 'open-subtab';

export interface WizardStepMeta {
  id: WizardStepId;
  label: string;
  /** Sub-tab that owns this step's full surface ("Open <sub-tab>" link target). */
  subTab: WizardSubTabId;
  subTabLabel: string;
  primaryAction: WizardPrimaryActionKind;
  /** Advisory node: rendered dashed + "Optional", never gates, no badge. */
  advisory?: boolean;
}

export const WIZARD_STEPS: readonly WizardStepMeta[] = [
  { id: 'author', label: 'Author', subTab: 'creator', subTabLabel: 'Creator', primaryAction: 'onboard-author' },
  { id: 'platforms', label: 'Platforms', subTab: 'platforms', subTabLabel: 'Platforms', primaryAction: 'open-subtab' },
  { id: 'pillars', label: 'Pillars', subTab: 'creator', subTabLabel: 'Creator', primaryAction: 'pillars' },
  { id: 'research', label: 'Research', subTab: 'themes', subTabLabel: 'Themes', primaryAction: 'open-subtab', advisory: true },
  { id: 'backlog', label: 'Backlog', subTab: 'backlog', subTabLabel: 'Backlog', primaryAction: 'generate-topics' },
  { id: 'strategy', label: 'Strategy', subTab: 'strategies', subTabLabel: 'Strategies', primaryAction: 'create-strategy' },
  { id: 'publications', label: 'Calendar slots', subTab: 'calendar', subTabLabel: 'Calendar', primaryAction: 'open-subtab' },
  { id: 'posts', label: 'Posts', subTab: 'strategies', subTabLabel: 'Strategies', primaryAction: 'open-subtab' },
  { id: 'assets', label: 'Assets', subTab: 'calendar', subTabLabel: 'Calendar', primaryAction: 'open-subtab', advisory: true },
] as const;

export interface WizardStepView extends WizardStepMeta {
  /** 1-based position in the visible 9-node spine. */
  position: number;
  /** Readiness record from the shared evaluator (undefined for 'research'). */
  readiness?: ContentPlanStepReadiness;
  /** Real-output completion badge (never "visited"). Advisory nodes: false. */
  complete: boolean;
  /** Forward-gated: a REQUIRED step earlier in the chain is not ok. */
  locked: boolean;
  /** First incomplete required step — the "current" step. */
  active: boolean;
  /** Step id whose completion unlocks this one (first blocking step). */
  unlockedBy?: WizardStepId;
  /** Human reason shown on the locked annotation. */
  lockReason?: string;
}

/**
 * Merge D12 readiness into the 9-node spine view.
 * Gating rule (designer.md): forward is gated by ANY earlier required step
 * that is not ok; backward (complete) steps are always free; advisory nodes
 * never gate and are never locked-annotated as blockers.
 */
export function buildWizardStepViews(
  steps: ContentPlanStepReadiness[],
): WizardStepView[] {
  const byId = new Map(steps.map((s) => [s.step, s]));
  const views: WizardStepView[] = [];
  let blocking: WizardStepView | null = null;
  let activeAssigned = false;

  WIZARD_STEPS.forEach((meta, index) => {
    const readiness = meta.id === 'research' ? undefined : byId.get(meta.id as ContentPlanFlowStep);
    const complete = Boolean(readiness?.ok) && !meta.advisory;
    const isRequired = Boolean(readiness?.required);
    const locked = Boolean(blocking) && !complete && !meta.advisory;
    const active = !activeAssigned && isRequired && !readiness?.ok && !locked;
    if (active) activeAssigned = true;

    const view: WizardStepView = {
      ...meta,
      position: index + 1,
      readiness,
      complete,
      locked,
      active,
      unlockedBy: locked && blocking ? blocking.id : undefined,
      lockReason: locked && blocking
        ? `${blocking.label} first — ${blocking.readiness?.detail ?? 'complete the earlier step'}`
        : undefined,
    };
    views.push(view);

    if (!blocking && isRequired && !readiness?.ok) {
      blocking = view;
    }
  });

  return views;
}

/** Strip summary: "Step N of 9 — <label> · <detail>". */
export function buildStripSummary(views: WizardStepView[]): string {
  const total = views.length;
  const active = views.find((v) => v.active);
  if (!active) {
    const requiredViews = views.filter((v) => v.readiness?.required);
    const allDone = requiredViews.length > 0 && requiredViews.every((v) => v.complete);
    if (allDone) return `All ${total} steps complete`;
    return 'Content plan flow';
  }
  const detail = active.readiness?.detail
    ?? (typeof active.readiness?.count === 'number' ? String(active.readiness.count) : '');
  return `Step ${active.position} of ${total} — ${active.label}${detail ? ` · ${detail}` : ''}`;
}
